import { SocketClientMessage } from '@okletsplay/core';
import { TOURNAMENT_ID_QUERY_KEY } from 'app/constants';
import { useSocketSubscriber } from 'app/hooks/socket';
import { useFetchData } from 'app/hooks/store';
import { Identifier } from 'app/interfaces';
import { TournamentEntity } from 'app/interfaces/entity';
import isEmpty from 'lodash/isEmpty';
import { WinningAnimationDialog } from 'modules/matches/components/WinningAnimationDialog';
import { TournamentModal } from 'modules/tournaments/components/TournamentModal';
import {
  useTeamPrize,
  useTournamentModal,
  useTournamentPlatformType,
} from 'modules/tournaments/hooks';
import {
  getTournamentById,
  updateTournament,
} from 'modules/tournaments/store/current-tournament/repository';
import { currentTournamentSelector } from 'modules/tournaments/store/current-tournament/selectors';
import React, { FC, PropsWithChildren, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Backdrop } from 'shared/components/Backdrop';
import { GameActivityType } from 'shared/enums';
import { GameActivityPaymentProvider } from 'modules/game-payments/providers';
import { usePlatformType } from 'modules/games/hooks/use-platform-type';

export const TournamentUrlQueryDetector: FC<PropsWithChildren<unknown>> = () => {
  const dispatch = useDispatch();
  const { search } = useLocation();
  const tournament: TournamentEntity = useSelector(currentTournamentSelector.tournament);
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const tournamentId = useMemo(() => searchParams.get(TOURNAMENT_ID_QUERY_KEY), [searchParams]);
  const { checkIsKnockoutEnabled } = useTournamentPlatformType();
  const { teamPrize } = useTeamPrize(tournament);
  const { openTournamentModal, removeTournamentIdQueryParam, openWinningAnimationModal } =
    useTournamentModal();

  const actions = useMemo(
    () => (tournamentId ? [getTournamentById(tournamentId)] : []),
    [tournamentId]
  );
  const { loading, fetch } = useFetchData<TournamentEntity>(actions, { fetchOnInit: false });

  const onCloseWinningAnimationHandler = useCallback(() => {
    if (!tournament?.id) {
      return;
    }

    dispatch(updateTournament(tournament?.id as string));
  }, [dispatch, tournament?.id]);

  const subscriptionHandler = useCallback(
    (identifier: Identifier) => {
      if (identifier !== tournamentId) {
        return;
      }

      fetch(false);
    },
    [fetch, tournamentId]
  );

  const subscriptions = useMemo(
    () => [
      {
        action: SocketClientMessage.Tournament,
        handler: subscriptionHandler,
      },
    ],
    [subscriptionHandler]
  );

  const platformType = useMemo(() => tournament.game?.platformType, [tournament]);
  const isAsyncGame = usePlatformType(platformType);

  useSocketSubscriber(subscriptions);

  useEffect(() => {
    if (isEmpty(tournament)) {
      return;
    }

    if (isAsyncGame && tournamentId) {
      openTournamentModal();
    }

    const isKnockoutTournament = checkIsKnockoutEnabled(platformType);
    if (isKnockoutTournament) {
      openWinningAnimationModal();
    }
  }, [
    dispatch,
    tournament,
    tournamentId,
    openTournamentModal,
    checkIsKnockoutEnabled,
    openWinningAnimationModal,
    isAsyncGame,
    platformType,
  ]);

  useEffect(() => {
    if (tournamentId) {
      fetch().catch(removeTournamentIdQueryParam);
    }
  }, [fetch, removeTournamentIdQueryParam, tournamentId]);

  if (loading) {
    return <Backdrop />;
  }

  const content = (
    <>
      {!isEmpty(tournament) && (
        <WinningAnimationDialog
          onClose={onCloseWinningAnimationHandler}
          prize={teamPrize}
          activity={GameActivityType.Tournament}
        />
      )}
      <TournamentModal />
    </>
  );

  return (
    <GameActivityPaymentProvider
      gameActivity={tournament}
      activityType={GameActivityType.Tournament}
    >
      {content}
    </GameActivityPaymentProvider>
  );
};
